import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ResponseHeader from "./ResponseHeader";
import QuestionNavigator from "./QuestionNavigator";
import ResponseCard from "./ResponseCard";
// import { MOCK_RESPONSES_DATA } from "./mockResponsesData";
import { useFormsContext } from "../../../../../../context/forms/FormsContextProvider";
import ResponsesByUser from "./ResponseByUser";
import ResponseByQuestion from "./ResponseByQuestion";

/**
 * Opens a new window containing only the content of the given element,
 * copies the page stylesheets for consistent rendering, and triggers print.
 */
function printElement(element: HTMLElement) {
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) return;

  // Collect stylesheets from the current page
  const stylesheets = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        if (sheet.href) {
          return `<link rel="stylesheet" href="${sheet.href}" />`;
        }
        const rules = Array.from(sheet.cssRules)
          .map((rule) => rule.cssText)
          .join("\n");
        return `<style>${rules}</style>`;
      } catch {
        // Cross-origin stylesheets may throw
        return sheet.href
          ? `<link rel="stylesheet" href="${sheet.href}" />`
          : "";
      }
    })
    .join("\n");

  printWindow.document.write(`
    <!DOCTYPE html>
    <html dir="rtl" lang="he">
      <head>
        <meta charset="utf-8" />
        <title>Print</title>
        ${stylesheets}
        <style>
          body {
            padding: 24px;
            background: #fff;
            font-family: inherit;
          }
          /* Remove shadows and overflow constraints for print */
          .shadow-md { box-shadow: none !important; }
          .overflow-y-auto { overflow: visible !important; }
          .max-h-\\[calc\\(100vh-170px\\)\\] { max-height: none !important; }
        </style>
      </head>
      <body>${element.innerHTML}</body>
    </html>
  `);
  printWindow.document.close();

  // Wait for stylesheets / images to load before printing
  printWindow.onload = () => {
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };
}

function ResponsesTab() {
  const { t } = useTranslation();
  const printableRef = useRef<HTMLDivElement>(null);
  const { selectedResponseTab } = useFormsContext();

  const handlePrint = useCallback(() => {
    if (printableRef.current) {
      printElement(printableRef.current);
    }
  }, []);

  return (
    <div className="flex flex-col h-full py-4 px-4">
      {/* Title + 3-dot menu */}
      <ResponseHeader onPrint={handlePrint} />

      {/* Border separator */}
      <div className="border-t border-gray80 mt-4 flex-shrink-0" />

      {/* Printable area: question + responses */}
      {selectedResponseTab == "question" ? (
        <ResponseByQuestion printableRef={printableRef} />
      ) : (
        <ResponsesByUser printableRef={printableRef} />
      )}
    </div>
  );
}

export default ResponsesTab;
